import {source, Map} from "azure-maps-control";
import {MapLayer} from "@esWebAPI/es/map/layer/MapLayer";
import {ILayerView, LayerInfo, LayerUpdateMetadata} from "@esWebAPI/es/model/Layer";
import {ColorThematic, SymbolThematic} from "@esWebAPI/es/model/Thematic";
import { ExpressionFilterSpecification } from "@maplibre/maplibre-gl-style-spec";
import {AzureHeatMapLayer} from "@esWebAPI/es/map/layer/azure/AzureHeatMapLayer";
import {DatasourceManager} from "@esWebAPI/es/map/module/DatasourceManager";
import {IEventChannel} from "@esWebAPI/es/model/Models";
import {PointLayer} from "@esWebAPI/es/map/layer/PointLayer";
import {AzureImageSpriteManager} from "@esWebAPI/es/map/layer/AzureImageSpriteManager";
import {ThematicStylerFactory} from "@esWebAPI/es/map/styler/ThematicStylerFactory";
import _ from "lodash";
import {AzureLabelLayer} from "@esWebAPI/es/map/layer/azure/AzureLabelLayer";


export class HeatMapLayer extends MapLayer {

    private readonly spriteManager: AzureImageSpriteManager;
    private readonly stylerFactory: ThematicStylerFactory;
    private heatMapDatasource: source.Source|undefined;
    private pointDatasource: source.Source|undefined;

    private _heatMapLayer: AzureHeatMapLayer|undefined;
    private _pointLayer: PointLayer|undefined;

    private _colorThematic: ColorThematic|undefined;
    private _symbolThematic: SymbolThematic|undefined;

    private debouncedOnUpdateThematic = _.debounce(this.onUpdateThematic.bind(this), 500);

    constructor(map: Map, layerView: ILayerView,
                layerInfo: LayerInfo,
                datasourceManager: DatasourceManager,
                spriteManager: AzureImageSpriteManager,
                stylerFactory: ThematicStylerFactory,
                eventChannel: IEventChannel) {

        super(map, layerView, layerInfo, datasourceManager, eventChannel);

        this.spriteManager = spriteManager;
        this.stylerFactory = stylerFactory;

        this.createLayers().then(() => {
            es.api.getEventChannel().addListener('onUpdateThematic', this.debouncedOnUpdateThematic);
            this.onReady.resolve();
        });
    }

    private async createLayers() {
        this._colorThematic = await this.stylerFactory.getColorStyler(this.layerView).getThematic(this.layerView);
        this._symbolThematic = await this.stylerFactory.getSymbolStyler().getThematic(this.layerView);

        this.heatMapDatasource =  await this.datasourceManager.create(
            this.layerView,
            this.layerInfo,
            false,
            this.getDatasourceFilter(this._colorThematic, this._symbolThematic),
            `es_${this.layerView.id}-heatmap`);

        this.pointDatasource =  await this.datasourceManager.create(
            this.layerView,
            this.layerInfo,
            !(this.layerView.styleSettings.defaultStyle.$type === "SymbolAndPolygonStyle"),
            this.getDatasourceFilter(this._colorThematic, this._symbolThematic));

        this._heatMapLayer = new AzureHeatMapLayer(this.map, this.layerView, this.layerInfo, this.datasourceManager)

        await this._heatMapLayer.create(this.heatMapDatasource);

        this._pointLayer = new PointLayer(this.map, this.layerView, this.layerInfo, this.datasourceManager,
            this.spriteManager, this.stylerFactory, this.eventChannel, this.pointDatasource, ["==", ['geometry-type'], "Point"]);

        await this._pointLayer.onReady;
    }

    public async update(layerUpdateMetadata: LayerUpdateMetadata): Promise<void> {
        if (this.heatMapDatasource && this._heatMapLayer && (
            layerUpdateMetadata.isCriteriaChanged ||
            layerUpdateMetadata.isFeatureInfoLastModifiedChanged ||
            layerUpdateMetadata.isMissingAttributes ||
            layerUpdateMetadata.isHeatmapOverlayPinsChanged)
        ) {
                this._heatMapLayer.remove();
                this.heatMapDatasource = await this.datasourceManager.recreate(
                    this.heatMapDatasource.getId(),
                    this.layerView,
                    this.layerInfo,
                    this.getDatasourceFilter(this._colorThematic, this._symbolThematic)
                );
                await this._heatMapLayer.recreate(this.layerView, this.layerInfo, this.heatMapDatasource)
        } else {
            this._heatMapLayer?.update(this.layerView, this.layerInfo);
        }

        this.eventChannel.fireEvent("onUpdateLayerComplete", this);
    }

    private async onUpdateThematic(thematic: any): Promise<void> {
        if (thematic.layerViewId !== this.layerView.id) {
            return Promise.resolve();
        }
        await this.updateThematics();
        return this.update({
            isThematicChanged: true,
            isFeatureInfoLastModifiedChanged: true
        });
    }

    private async updateThematics(): Promise<void> {
        this._colorThematic = await this.stylerFactory.getColorStyler(this.layerView).getThematic(this.layerView);
        //this._sizeThematic = await this.stylerFactory.getSizeStyler().getThematic(this.layerView);
        this._symbolThematic = await this.stylerFactory.getSymbolStyler().getThematic(this.layerView);
    }

    protected getColorThematicFilter(colorThematic?: ColorThematic): ExpressionFilterSpecification | undefined {
        return this.stylerFactory.getColorStyler(this.layerView).getFilterExpression(this.layerView, colorThematic);
    }

    protected getSymbolThematicFilter(symbolThematic?: SymbolThematic): ExpressionFilterSpecification | undefined {
        return this.stylerFactory.getSymbolStyler().getFilterExpression(this.layerView, symbolThematic);
    }

    protected hideFeature(): void {}

    protected showFeatures(): void {}

    public getLayerIds(): string[] {
        const layerIds: string[] = [];
        if (this._heatMapLayer) {
            layerIds.push(this._heatMapLayer.getLayerId());
        }
        if (this._pointLayer) {
            layerIds.push(...this._pointLayer.getLayerIds());
        }
        return layerIds;
    }

    public destroy(): void {
        super.destroy()
        this._heatMapLayer?.remove()
        this._pointLayer?.destroy();
        if (this.heatMapDatasource) {
            this.datasourceManager.remove(this.heatMapDatasource.getId());
        }
        if (this.pointDatasource) {
            this.datasourceManager.remove(this.pointDatasource.getId());
        }
    }

    public static getIntensityRange(layerInfo: LayerInfo): [number, number] {

        if (layerInfo.featureInfo.count === 0) {
            return [0, 1];
        }

        // calc bounding box
        const bounds = layerInfo.featureInfo.bounds;
        const width = Math.abs(bounds[2] - bounds[0]);
        const height = Math.abs(bounds[3] - bounds[1]);
        const datasourceArea = width * height;

        // "golden" ratio - not really the golden ratio but golden in this case (we hope)
        const featureDensity = layerInfo.featureInfo.count / datasourceArea;

        const scalingFactor = Math.min(Math.max(1 / featureDensity, 0.1), 10);

        // Calculate maxIntensity with a more reasonable formula that avoids extremes
        const maxIntensity = Math.min(Math.max(scalingFactor, 4), 5);

        //getIntensityRange_old(layerInfo);
        es.log.log("getIntensityRange", [0, maxIntensity]);
        return [0, maxIntensity];
    }

    public get pointLayer(): PointLayer | undefined {
        return this._pointLayer;
    }

    public get labelLayer(): AzureLabelLayer | undefined {
        return this._pointLayer?.labelLayer;
    }

    public getActiveAzureLayerId() {
        return this._pointLayer?.getActiveAzureLayerId();
    }

    public async getClusterLeavesFeats(clusterId: number): Promise<any[]> {
        return ((this._pointLayer) as PointLayer).getClusterLeavesFeats(clusterId);
    }

    public async getClusterLeavesFeatIds(clusterId: number): Promise<number[]> {
        return ((this._pointLayer) as PointLayer).getClusterLeavesFeatIds(clusterId);
    }
}